// Extension manifests: the single source of truth, shared by the frontend
// (src/extensions/manifests.ts) and edge functions (partnr-chat). Pure data on
// purpose: no imports, so Deno and Vite can both load this file. The
// ExtensionManifest type lives in src/extensions/types.ts; registry.test.ts
// checks these objects against it.

// Top Creatives: the Apify scraper. Server side lives in
// supabase/functions/apify-scrape and classify-creative; keep EXTENSION_ID,
// DEFAULT_ENABLED and DAILY_LIMIT there in sync with this entry.
const creativeScout = {
  id: 'creative-scout',
  name: 'Top Creatives',
  description:
    'Pull the best-performing TikTok, Instagram and Facebook posts from any account onto a new board, scored for hook and hold.',
  icon: 'Sparkles',
  accent: '#f97316',
  version: 1,
  defaultEnabled: false,
  requiresSecrets: ['APIFY_API_TOKEN', 'OPENROUTER_API_KEY'],
  settings: {
    lookbackDays: {
      type: 'select',
      label: 'Default lookback window',
      options: [
        { value: '7', label: 'Last 7 days' },
        { value: '14', label: 'Last 14 days' },
        { value: '30', label: 'Last 30 days' },
      ],
      default: '30',
    },
    engagementThreshold: {
      type: 'number',
      label: 'Default minimum engagement',
      min: 1,
      max: 20,
      step: 0.5,
      unit: '%',
      default: 4,
    },
    vertical: {
      type: 'text',
      label: 'Default vertical',
      placeholder: 'e.g. skincare, fitness, SaaS',
      default: '',
      help: 'Helps the scoring model judge the creatives against your market.',
    },
  },
  itemTypes: ['scraped_creative'],
  actions: [
    {
      id: 'analyze_accounts',
      label: 'Analyze accounts',
      description:
        'Scrape recent posts from up to 10 social accounts, keep the top performers by engagement rate and place them on a new board.',
      function: 'apify-scrape',
      cost: 'paid',
      dailyLimit: 5,
      input: {
        type: 'object',
        properties: {
          sources: {
            type: 'array',
            description: 'Accounts to analyze.',
            maxItems: 10,
            items: {
              type: 'object',
              properties: {
                platform: { type: 'string', enum: ['tiktok', 'instagram', 'facebook'] },
                account_handle: { type: 'string', description: 'Handle without the @.' },
              },
              required: ['platform', 'account_handle'],
            },
          },
          lookback_days: { type: 'integer', enum: [7, 14, 30] },
          engagement_threshold: {
            type: 'number',
            description: 'Minimum engagement rate as a fraction, e.g. 0.04 for 4%.',
            minimum: 0.01,
            maximum: 0.2,
          },
          vertical: { type: 'string', description: 'Market or niche, e.g. skincare.' },
        },
        required: ['sources'],
      },
    },
    {
      // Same edge function: a body with `url` and no `sources` runs this action.
      id: 'analyze_post',
      label: 'Pull full post',
      description:
        'Scrape one Instagram, TikTok or Facebook post or reel from its URL (media, caption, author, views, likes, comments, shares) and score it for hook and hold. Upgrades the link card in place when item_id is given, otherwise adds a new card to board_id.',
      function: 'apify-scrape',
      cost: 'paid',
      dailyLimit: 5,
      input: {
        type: 'object',
        properties: {
          url: { type: 'string', description: 'Full URL of a single post, reel or video.' },
          item_id: { type: 'string', description: 'Optional. The link card for this URL, to upgrade in place.' },
          board_id: { type: 'string', description: 'Board for a new card when there is no item_id.' },
          vertical: { type: 'string', description: 'Market or niche, e.g. skincare.' },
        },
        required: ['url'],
      },
    },
  ],
};

// X Bookmarks: syncs the user's own X bookmarks into an "X Bookmarks" board.
// Server side: x-auth (connect), x-bookmarks-sync (one user), x-bookmarks-cron
// (everyone, run daily by extensions-cron). Free for the user, so on by default;
// it does nothing until they connect an X account. Keep DEFAULT_ENABLED in
// x-bookmarks-sync and x-bookmarks-cron in sync with this entry.
const xBookmarks = {
  id: 'x-bookmarks',
  name: 'X Bookmarks',
  description:
    'Sync the posts you bookmark on X into an "X Bookmarks" board, automatically every day.',
  icon: 'Bookmark',
  accent: '#0f1419',
  version: 1,
  defaultEnabled: true,
  requiresSecrets: ['X_CLIENT_ID', 'X_CLIENT_SECRET'],
  settings: {},
  itemTypes: [],
  schedule: { function: 'x-bookmarks-cron', frequency: 'daily' },
  actions: [
    {
      id: 'sync_now',
      label: 'Sync bookmarks now',
      description:
        'Pull the user\'s X (Twitter) bookmarks into their "X Bookmarks" board right now, instead of waiting for the daily sync. Only works once they have connected an X account in Extensions.',
      function: 'x-bookmarks-sync',
      cost: 'free',
      input: {
        type: 'object',
        properties: {
          mode: {
            type: 'string',
            enum: ['daily', 'backfill'],
            description: 'daily: newest bookmarks since the last sync (use this unless asked). backfill: go back up to 6 months.',
          },
        },
        required: ['mode'],
      },
    },
  ],
};

// Focus Timer: a pomodoro timer that floats over the canvas, with history and a
// calendar view. Runs entirely in the browser (src/extensions/focus-timer);
// finished sessions sync to the focus_sessions table. No actions, so Partnr
// gets no tool for it.
const focusTimer = {
  id: 'focus-timer',
  name: 'Focus Timer',
  description:
    'A pomodoro timer that floats over your canvas. Name what you are working on, add time when you need it, and review every session in a list or on a calendar.',
  icon: 'Timer',
  accent: '#e5484d',
  version: 1,
  defaultEnabled: true,
  requiresSecrets: [],
  settings: {
    focusMinutes: {
      type: 'number',
      label: 'Focus length',
      min: 1,
      max: 240,
      step: 1,
      unit: 'min',
      default: 25,
    },
    breakMinutes: {
      type: 'number',
      label: 'Break length',
      min: 1,
      max: 60,
      step: 1,
      unit: 'min',
      default: 5,
    },
    sound: {
      type: 'select',
      label: 'Sound when time is up',
      options: [
        { value: 'on', label: 'Chime' },
        { value: 'off', label: 'Silent' },
      ],
      default: 'on',
    },
  },
  itemTypes: [],
  actions: [],
};

// Color Palette: the main colors of an image as circles under it (as in
// mymind); click one to copy its code. Runs entirely in the browser
// (src/extensions/color-palette). No actions, so Partnr gets no tool for it.
const colorPalette = {
  id: 'color-palette',
  name: 'Color Palette',
  description:
    'Select an image to see its main colors as circles underneath. Click a circle to copy the color code.',
  icon: 'Palette',
  accent: '#8b5cf6',
  version: 1,
  defaultEnabled: false,
  requiresSecrets: [],
  settings: {
    colors: {
      type: 'number',
      label: 'Colors per image',
      min: 3,
      max: 8,
      step: 1,
      default: 5,
    },
    format: {
      type: 'select',
      label: 'Copy as',
      options: [
        { value: 'hex', label: 'HEX (#8B5CF6)' },
        { value: 'rgb', label: 'RGB (rgb(139, 92, 246))' },
      ],
      default: 'hex',
    },
  },
  itemTypes: [],
  actions: [],
};

// Pinterest Import: a Pinterest board's pins onto the canvas as image cards.
// Board pages draw their pins with JavaScript, so link previews and Partnr's
// read_url only see the board's name. Server side: supabase/functions/
// pinterest-import (an Apify board scraper); keep DEFAULT_ENABLED and
// DAILY_LIMIT there in sync with this entry.
const pinterestImport = {
  id: 'pinterest-import',
  name: 'Pinterest Import',
  description:
    'Paste a Pinterest board link and its pins land on your canvas as images, in a folder named after the board or as one cluster.',
  icon: 'Pin',
  accent: '#e60023',
  version: 1,
  defaultEnabled: false,
  requiresSecrets: ['APIFY_API_TOKEN'],
  settings: {},
  itemTypes: [],
  actions: [
    {
      id: 'import_board',
      label: 'Import a board',
      description:
        "Bring the pins of a Pinterest board (or board section) onto the canvas as image cards, each linking back to its pin. Use it whenever the user shares a Pinterest board link (pinterest.com/<name>/<board>/, any country domain, or a pin.it short link) and wants its pins, images or contents: read_url and web_search cannot list a board's pins. Runs in the background; the pins land a minute or two after it starts. Not for a single pin: use add_links for those.",
      function: 'pinterest-import',
      cost: 'paid',
      dailyLimit: 10,
      resultNote:
        'Say the pins will land in a minute or two (in a new folder named after the board, or as a cluster on this board) and that a notice appears when they do.',
      input: {
        type: 'object',
        properties: {
          url: { type: 'string', description: 'The board link exactly as the user gave it. pin.it links are fine.' },
          into_folder: {
            type: 'boolean',
            description:
              'true (default): a new folder named after the board, on the open board. false: the pins go straight onto the open board as one cluster; use false when the user asks for them here, on this board or as a cluster.',
          },
          max_pins: { type: 'integer', minimum: 1, maximum: 200, description: 'How many pins, newest first. Default 100.' },
          board_id: { type: 'string', description: 'Optional. A folder_id to import into instead of the open board.' },
        },
        required: ['url'],
      },
    },
  ],
};

export const EXTENSION_MANIFESTS = [creativeScout, pinterestImport, xBookmarks, focusTimer, colorPalette];
